import { useCallback, useEffect, useState } from 'react';
import GeneralCvIntro from '@components/client-components/general-cv/GeneralCvIntro';
import LogoText from '@components/client-components/logo-text/LogoText';
import MainImage from '@components/client-components/main-image/MainImage';
import Positions from '@components/client-components/positions/Positions';
import StateEnum from '@constants/state.enum';
import useNotification from '@hooks/notification/useNotification';
import ClientInfoModel from '@models/clientInfo';
import { getClientInfo } from '@services/form/form-http.service';
import { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { getClientHomepage } from '@services/homepage/homepage-http.service';
import ClientHomepageModel from '@models/clientHomepage';
import { Spin } from 'antd';
import SendCv from '@components/client-components/send-cv/SendCv';
import Script from 'next/script';
import getConfig from 'next/config';
import styles from './Career.module.scss';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA_KEY },
} = getConfig();

const defaultClientHomepage: ClientHomepageModel = {
  color: '#ff2458',
  description: '',
  image_url: '',
  main_text: '',
  subtitle: '',
};

const CareerPage: NextPage = () => {
  const router = useRouter();
  const slugClient = router.query.slug ? (router.query.slug as string) : '';
  const [, setLoading] = useState<boolean>(true);
  const [clientInfo, setClientInfo] = useState<ClientInfoModel>();
  const [showForm, setShowForm] = useState<boolean>(!!router.query.showform);
  const [clientHomepage, setClientHomepage] = useState<ClientHomepageModel>(defaultClientHomepage);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );
  const [companyNotFoundError] = useNotification(
    StateEnum.ERROR,
    'Company not found',
    'Oops, the company you are trying does not exist.',
  );

  const fetchClientInfo = useCallback(async () => {
    if (slugClient) {
      try {
        const client: ClientInfoModel = await getClientInfo(slugClient);
        setClientInfo(client);
      } catch (e: any) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        e.ErrorMessage === 'company name not found' && companyNotFoundError();
        setLoading(true);
        generalError();
      }
    }
  }, [slugClient]);

  const fetchClientHomepage = useCallback(async () => {
    if (slugClient) {
      try {
        const clientHomepageElem: ClientHomepageModel = await getClientHomepage(slugClient);
        setClientHomepage(clientHomepageElem);
      } catch (e: any) {
        generalError();
      }
    }
  }, [slugClient]);

  useEffect(() => {
    setLoading(true);
    fetchClientInfo();
    fetchClientHomepage();
  }, [fetchClientHomepage, fetchClientInfo]);

  return (
    <>
      <Script src={`https://www.google.com/recaptcha/api.js?render=${GOOGLE_RECAPTCHA_KEY}`} />
      <section className={styles.compWrap}>
        {!clientInfo ? (
          <Spin size="large" />
        ) : (
          <div className={styles.details}>
            <MainImage
              clientColor={clientHomepage?.color}
              mainImage={clientHomepage?.image_url}
              mainText={clientHomepage?.main_text}
              setShowForm={setShowForm}
            />
            <LogoText
              logo={clientInfo?.logo_url}
              clientColor={clientHomepage?.color}
              title={clientHomepage?.subtitle}
              description={clientHomepage?.description}
            />
            <div className={styles.positionsOuterWrap}>
              <Positions slug={slugClient} clientColor={clientHomepage?.color} />
            </div>
            <GeneralCvIntro clientColor={clientHomepage?.color} />
            <SendCv
              slug={slugClient}
              clientColor={clientHomepage?.color}
              showForm={showForm}
              setShowForm={setShowForm}
            />
          </div>
        )}
      </section>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    return {
      props: {
        ...(await serverSideTranslations(context.locale!, ['common', 'form'])),
      },
    };
  } catch (err: any) {
    return {
      props: {
        errorCode: err.status || err?.response?.status || 500,
        ...(await serverSideTranslations(context.locale!, ['common', 'form'])),
      },
    };
  }
};

export default CareerPage;
